// src/bus/cancelation.ts
var CANCELED = /* @__PURE__ */ Symbol.for("@xone-web/events/canceled");
function isCancellationValue(value) {
  return value === false;
}

// src/errors/XoneEventError.ts
var XONE_EVENT_ERROR_CODES = {
  /** El evento solicitado no existe en el catalogo. */
  UNKNOWN_EVENT: "XONE.EVENTS.UNKNOWN_EVENT",
  /** Handler invalido (no es una funcion). */
  INVALID_HANDLER: "XONE.EVENTS.INVALID_HANDLER",
  /** Contexto de evento invalido. */
  INVALID_CONTEXT: "XONE.EVENTS.INVALID_CONTEXT",
  /** Error fatal durante la ejecucion de un handler. */
  HANDLER_FAILURE: "XONE.EVENTS.HANDLER_FAILURE"
};
var XoneEventError = class _XoneEventError extends Error {
  code;
  eventName;
  cause;
  constructor(message, options) {
    super(message);
    this.name = "XoneEventError";
    this.code = options.code;
    this.eventName = options.eventName;
    this.cause = options.cause;
    Object.setPrototypeOf(this, _XoneEventError.prototype);
  }
};

// src/bus/XoneEventBus.ts
var XoneEventBus = class {
  handlers = /* @__PURE__ */ new Map();
  logger;
  constructor(options = {}) {
    this.logger = options.logger ?? console;
  }
  // -------------------------------------------------------------------------
  // Registro / desuscripcion
  // -------------------------------------------------------------------------
  /**
   * Registra un handler. Devuelve un `Unsubscribe` que lo retira.
   *
   * Los handlers se ejecutan en orden de registro (FIFO).
   */
  on(eventName, handler) {
    if (typeof eventName !== "string" || eventName.length === 0) {
      throw new XoneEventError("eventName debe ser un string no vacio.", {
        code: XONE_EVENT_ERROR_CODES.UNKNOWN_EVENT,
        eventName: String(eventName)
      });
    }
    if (typeof handler !== "function") {
      throw new XoneEventError("handler debe ser una funcion.", {
        code: XONE_EVENT_ERROR_CODES.INVALID_HANDLER,
        eventName
      });
    }
    const list = this.handlers.get(eventName) ?? [];
    list.push(handler);
    this.handlers.set(eventName, list);
    return () => this.off(eventName, handler);
  }
  /**
   * Registra un handler que se ejecuta UNA sola vez. Se desuscribe
   * automaticamente despues de la primera ejecucion (exitosa o con error).
   */
  once(eventName, handler) {
    const wrapper = async (ctx) => {
      this.off(eventName, wrapper);
      return await handler(ctx);
    };
    return this.on(eventName, wrapper);
  }
  /** Desuscribe un handler por referencia. */
  off(eventName, handler) {
    const list = this.handlers.get(eventName);
    if (!list) return;
    const idx = list.indexOf(handler);
    if (idx >= 0) list.splice(idx, 1);
    if (list.length === 0) this.handlers.delete(eventName);
  }
  /**
   * Limpia handlers. Si se omite `eventName`, vacia el bus entero.
   */
  clear(eventName) {
    if (eventName === void 0) {
      this.handlers.clear();
      return;
    }
    this.handlers.delete(eventName);
  }
  /** Cuenta de handlers registrados para un evento. */
  listenerCount(eventName) {
    return this.handlers.get(eventName)?.length ?? 0;
  }
  /** Devuelve `true` si hay al menos un handler registrado para `eventName`. */
  hasHandlers(eventName) {
    return this.listenerCount(eventName) > 0;
  }
  // -------------------------------------------------------------------------
  // Emisores (async-first, await secuencial)
  // -------------------------------------------------------------------------
  /** Emite un evento de ciclo de vida de objeto. */
  async emitObject(eventName, ctx) {
    this.assertContext(ctx, eventName);
    return await this.runChain(eventName, ctx);
  }
  /** Emite un coll-action. */
  async emitCollection(eventName, ctx) {
    this.assertContext(ctx, eventName);
    return await this.runChain(eventName, ctx);
  }
  /** Emite un evento declarativo de control (`EventOn*`). */
  async emitControl(eventName, ctx) {
    this.assertContext(ctx, eventName);
    return await this.runChain(eventName, ctx);
  }
  /** Emite un nodo custom (`self.executeNode("calcularTotales")`). */
  async emitCustomNode(nodeName, ctx) {
    if (typeof nodeName !== "string" || nodeName.length === 0) {
      throw new XoneEventError("nodeName debe ser un string no vacio.", {
        code: XONE_EVENT_ERROR_CODES.UNKNOWN_EVENT,
        eventName: String(nodeName)
      });
    }
    this.assertContext(ctx, nodeName);
    return await this.runChain(nodeName, ctx);
  }
  // -------------------------------------------------------------------------
  // Internos
  // -------------------------------------------------------------------------
  assertContext(ctx, eventName) {
    if (ctx === null || typeof ctx !== "object") {
      throw new XoneEventError(
        `Contexto invalido para el evento '${eventName}'.`,
        {
          code: XONE_EVENT_ERROR_CODES.INVALID_CONTEXT,
          eventName
        }
      );
    }
  }
  /**
   * Recorre la lista de handlers asociada al evento, secuencialmente con
   * `await`. Cumple las invariantes documentadas:
   *  - Orden FIFO.
   *  - `false` cancela y devuelve `canceled: true`.
   *  - Excepciones se capturan y NO interrumpen la cadena.
   *  - `EventResult.handlersExecuted` cuenta las invocaciones COMPLETAS
   *    (incluido el handler que cancelo o que lanzo).
   */
  async runChain(eventName, ctx) {
    const list = this.handlers.get(eventName);
    if (!list || list.length === 0) {
      return Object.freeze({
        canceled: false,
        returnValue: void 0,
        handlersExecuted: 0,
        errors: []
      });
    }
    const snapshot = list.slice();
    let returnValue = void 0;
    let executed = 0;
    const errors = [];
    for (const handler of snapshot) {
      executed += 1;
      let result;
      try {
        result = await handler(ctx);
      } catch (err) {
        this.logger.error(
          `[xone-events] Handler de '${eventName}' lanzo:`,
          err
        );
        errors.push(err);
        continue;
      }
      if (isCancellationValue(result)) {
        return Object.freeze({
          canceled: true,
          returnValue: false,
          handlersExecuted: executed,
          errors: Object.freeze(errors.slice())
        });
      }
      if (result !== void 0) {
        returnValue = result;
      }
    }
    return Object.freeze({
      canceled: false,
      returnValue,
      handlersExecuted: executed,
      errors: Object.freeze(errors.slice())
    });
  }
};
function createXoneEventBus(options = {}) {
  return new XoneEventBus(options);
}

// src/bus/lifecycleOrder.ts
var LIFECYCLE_ORDER = Object.freeze([
  "create",
  "load",
  "before-edit",
  "after-edit",
  "resume-edit",
  "onback",
  "before-save",
  "after-save",
  "before-delete",
  "after-delete",
  "unload"
]);
var OBJECT_EVENTS = /* @__PURE__ */ new Set([
  "create",
  "load",
  "unload",
  "before-edit",
  "after-edit",
  "resume-edit",
  "onback",
  "before-save",
  "after-save",
  "before-delete",
  "after-delete",
  "onchange",
  "selecteditem",
  "longpressitem"
]);
var COLLECTION_EVENTS = /* @__PURE__ */ new Set([
  "onlogon",
  "onlogoff",
  "onstartreplica",
  "onendreplica",
  "onpushregistered",
  "onpushmessage"
]);
var CANCELABLE_EVENTS = /* @__PURE__ */ new Set([
  "before-save",
  "before-delete",
  "before-edit",
  "onback"
]);

// src/locals/controlEventLocals.ts
var CONTROL_EVENT_LOCALS = Object.freeze({
  OnClick: ["Sender", "params"],
  OnChildClick: ["Sender", "Child"],
  OnParentClick: ["Sender", "Parent"],
  OnContentItemClick: ["Sender", "Item"],
  OnReorderItem: ["Sender", "From", "To"],
  OnItemRemoved: ["Sender", "Item"],
  OnDismissItemClick: ["Sender", "Item"],
  OnItemsChanged: ["Sender"],
  OnFocusChanged: ["Sender", "HasFocus"],
  OnTextChanged: ["Sender", "Text"],
  OnTextLengthChanged: ["Sender", "Length"],
  OnEditorAction: ["Sender", "Action"],
  OnKeyDown: ["Sender", "KeyCode"],
  OnTouch: ["Sender", "X", "Y"],
  OnScroll: ["Sender", "dx", "dy"],
  OnLinkClicked: ["Sender", "Url"],
  OnDrop: ["Sender", "Source", "Target"],
  OnCellDrop: ["Sender", "Source", "Target"],
  OnMapDrop: ["Sender", "Source", "Target", "Lat", "Lon"],
  OnSignatureAcquired: ["Sender", "Path"],
  OnDocumentSigned: ["Sender", "Path", "Result"],
  OnDistanceMeter: ["Sender", "Distance"],
  OnCodeScanned: ["Sender", "Code", "Format"],
  OnRawCodeScanned: ["Sender", "Raw"],
  OnLicensePlateScanned: ["Sender", "Plate", "Region"],
  OnImageDetected: ["Sender", "Path"],
  OnMachineLearningResults: ["Sender", "Results"],
  OnConsoleMessage: ["Sender", "Message"],
  OnPageError: ["Sender", "Url", "Message"],
  OnPageLoadFinished: ["Sender", "Url"],
  OnWebViewMessage: ["Sender", "Message"],
  OnLocationChanged: [
    "Sender",
    "Lat",
    "Lon",
    "Alt",
    "Accuracy",
    "Bearing",
    "Speed"
  ],
  OnLocationReady: ["Sender"],
  OnStreetViewStatusChanged: ["Sender", "Status"],
  OnMapReady: ["Sender", "Map"],
  OnMapClicked: ["Sender", "Lat", "Lon"],
  OnMapLongClicked: ["Sender", "Lat", "Lon"],
  OnMapMoveStarted: ["Sender", "Lat", "Lon"],
  OnMapMoveEnded: ["Sender", "Lat", "Lon"],
  OnMapZoomChanged: ["Sender", "Zoom"],
  OnMarkerClick: ["Sender", "Marker"],
  OnMarkerDragEnd: ["Sender", "Marker", "Lat", "Lon"],
  OnClusterClicked: ["Sender", "Cluster"],
  OnLineClicked: ["Sender", "Line"],
  OnBeforeEdit: ["Sender", "Object"],
  OnAfterEdit: ["Sender", "Object"],
  OnResumeEdit: ["Sender", "Object"],
  OnDrawerOpened: ["Sender", "State"],
  OnDrawerClosed: ["Sender", "State"],
  OnDrawerSlide: ["Sender", "State", "Offset"],
  OnDrawerStateChanged: ["Sender", "State"],
  OnBottomSheetStateChanged: ["Sender", "State"],
  OnError: ["Sender", "Error"],
  OnPushRegistered: ["Token"]
});
var CONTROL_EVENT_NAMES = Object.freeze(
  Object.keys(CONTROL_EVENT_LOCALS)
);
function isKnownControlEvent(name) {
  return Object.prototype.hasOwnProperty.call(CONTROL_EVENT_LOCALS, name);
}

// src/registry/ScriptRegistry.ts
function makeEventKey(collectionName, eventName) {
  return `${collectionName}:${eventName}`;
}
var ScriptRegistry = class {
  entries = /* @__PURE__ */ new Map();
  register(script) {
    if (typeof script.eventName !== "string" || script.eventName.length === 0) {
      throw new TypeError("eventName debe ser un string no vacio.");
    }
    if (typeof script.collectionName !== "string" || script.collectionName.length === 0) {
      throw new TypeError("collectionName debe ser un string no vacio.");
    }
    if (typeof script.scriptSource !== "string") {
      throw new TypeError("scriptSource debe ser un string.");
    }
    const key = makeEventKey(script.collectionName, script.eventName);
    const list = this.entries.get(key) ?? [];
    const entry = {
      eventName: script.eventName,
      collectionName: script.collectionName,
      nodeName: script.nodeName,
      scriptSource: script.scriptSource,
      refreshAfter: script.refreshAfter,
      showWaitDialog: script.showWaitDialog,
      waitDialogText: script.waitDialogText
    };
    list.push(entry);
    this.entries.set(key, list);
    return () => {
      const current = this.entries.get(key);
      if (!current) return;
      const idx = current.indexOf(entry);
      if (idx >= 0) current.splice(idx, 1);
      if (current.length === 0) this.entries.delete(key);
    };
  }
  /** Devuelve la lista de scripts asociados a (`collectionName`, `eventName`). */
  get(collectionName, eventName) {
    return this.entries.get(makeEventKey(collectionName, eventName)) ?? [];
  }
  /** Elimina todos los scripts asociados a una coleccion (uso de tear-down). */
  clearCollection(collectionName) {
    const prefix = `${collectionName}:`;
    for (const key of Array.from(this.entries.keys())) {
      if (key.startsWith(prefix)) this.entries.delete(key);
    }
  }
  /** Limpia el registro completo. */
  clear() {
    this.entries.clear();
  }
};
function registerScript(eventName, scriptSource, options) {
  if (!options || typeof options !== "object") {
    throw new TypeError("options es obligatorio.");
  }
  if (!options.runtime) {
    throw new TypeError("options.runtime es obligatorio.");
  }
  const registry = options.registry ?? defaultScriptRegistry;
  return registry.register({
    eventName,
    collectionName: options.collectionName,
    nodeName: options.nodeName,
    scriptSource,
    refreshAfter: options.refreshAfter,
    showWaitDialog: options.showWaitDialog,
    waitDialogText: options.waitDialogText
  });
}
var defaultScriptRegistry = new ScriptRegistry();
export {
  CANCELABLE_EVENTS,
  CANCELED,
  COLLECTION_EVENTS,
  CONTROL_EVENT_LOCALS,
  CONTROL_EVENT_NAMES,
  LIFECYCLE_ORDER,
  OBJECT_EVENTS,
  ScriptRegistry,
  XONE_EVENT_ERROR_CODES,
  XoneEventBus,
  XoneEventError,
  createXoneEventBus,
  defaultScriptRegistry,
  isCancellationValue,
  isKnownControlEvent,
  makeEventKey,
  registerScript
};
//# sourceMappingURL=index.js.map